import '@babel/polyfill';
import { LicenseManager } from '@ag-grid-enterprise/core'; // eslint-disable-line
import { createInstance, OptimizelyProvider } from '@optimizely/react-sdk'; // eslint-disable-line
import * as Sentry from '@sentry/react';
// imports modules from common package
import {
	Authentication,
	Chatbot,
	Error as ErrorModule,
	FeatureFlags,
	Layout,
	Modal,
	Redirect,
	Toast,
	UI,
	Utils,
} from 'common';
import { isObject } from 'lodash';
import React, { Component } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter as Router } from 'react-router-dom';
import MainView from './components/MainView.component';
import AccountCommunication from './components/utils/AccountCommunication';

Sentry.init({
	dsn: 'https://2bf0cdb2c7121a2c231715a123b5bbb1@o106853.ingest.us.sentry.io/4509831239499776',
	// Setting this option to true will send default PII data to Sentry.
	// For example, automatic IP address collection on events
	sendDefaultPii: true,
	environment:
		process.env.NODE_ENV === 'production' ? 'account' : 'account-dev',
});

const { ModalContainer } = Modal;
const {
	Analytics,
	Auth0Util,
	Session,
	StyleLoader,
	Util,
	Navigation,
	CustStorage,
	BillingPlans,
	withLayoutAndModalContexts,
} = Utils;
const { ChatbotInitializer, SierraInitializer, SierraChatLauncher } = Chatbot;
const { InitializeLaunchDarkly } = FeatureFlags;
const { Spinner } = UI;
const { SignIn } = Authentication;
const { EndiciaRedirect } = Redirect;
const { ToastProvider } = Toast;

LicenseManager.setLicenseKey(__AG_GRID_LICENSE__);

export default class App extends Component {
	constructor() {
		super();

		// hardcoded Settings.Page type for now
		const Page = 'account';

		// Check if on Endicia domain - skip auth calls but still load styles
		const redirectToStamps = Util.isEndiciaDomain();

		// the following search keys are case insensitive
		const isInModal = Util.getQueryParamByName('inModal') !== null;
		const storeContext = Util.getQueryParamByName('storeContext');
		const winClientVersion = Util.getQueryParamByName('cver');
		const SSOError = Util.getQueryParamByName('sso_error_code');
		const clientType = Util.getQueryParamByName('client-type');

		const data = {
			winClientVersion,
			InternalAccountInfo: true,
		};

		this.state = {
			mask: false,
			showSignIn: false,
			data,
			ssoError: SSOError,
			auth0Error: false,
			directStoreSignin: Util.isInRoute('signin'),
			externalData: { clientType },

			// `true` to hide Sign-In view
			optionalAuth: this.shouldByPassSignIn(),
			optimizelyClient: null,

			stylesLoaded: false,
			findUsername: Util.isInRoute('recoverusername'),
			resetPassword: Util.getQueryParamByName('token') !== null,
			recoverpassword: Util.isInRoute('recoverpassword'),
			// custom error page for auth0 error page
			auth0CustomError: Util.isInRoute('autherror') === true,
		};

		Session.init(
			{ isInModal, Page, storeContext, fbAppId: 794486702036528 }, // hardcoded facebook fbAppId for now (Got from marketing team)
			this.accountTimeout
		);

		AccountCommunication.init({
			resetTimeout: Session.resetTimeout,
			triggerTimeout: Session.triggerTimeout,
		});

		// Skip verifyAuthAndLoad for Endicia domain, but still load styles
		if (redirectToStamps) {
			StyleLoader.loadTheme(() => {
				// Directly set state in constructor (setState not allowed here)
				this.state.mask = false;
				this.state.stylesLoaded = true;
			});
		} else {
			Session.verifyAuthAndLoad(data, this.sessionLoadCallback);
		}
	}

	sessionLoadCallback = async () => {
		const { externalData } = this.state;
		const sessionData = Session.getData();
		const { isSignedIn, settings } = sessionData;
		const optimizelyClient = createInstance({ sdkKey: settings.OptimizelyKey });

		document.title = 'Stamps.com | Account';
		ErrorModule.ErrorService.init();
		StyleLoader.loadTheme(this.setState({ mask: true, stylesLoaded: true }));

		const completeAuth = Util.isInRoute('completeAuth') === true;

		if (!isSignedIn && completeAuth) {
			const completeAuthResponse = await Auth0Util.completeAuth();

			if (completeAuthResponse?.isSuccessful === true) {
				Navigation.redirect(completeAuthResponse.url);
				return;
			} else {
				this.setState({
					auth0Error: true,
				});
			}
		}

		if (isSignedIn) {
			await CustStorage.init();
			await BillingPlans.init();
		}

		Analytics.set(sessionData, externalData);

		const LDProvider = await InitializeLaunchDarkly();

		this.setState(({ directStoreSignin, optionalAuth, showSignIn }) => ({
			// do not force signIn view if optionalAuth is true
			showSignIn: !isSignedIn || directStoreSignin ? !optionalAuth : showSignIn,

			// hide spinner after Session ajax response comes back
			mask: false,
			optimizelyClient,
			analyticsMetadata: Analytics.metadata,
			LDProvider,
		}));
	};

	/**
	 * Initializes Sierra chatbot for Account view with Header component shown.
	 */
	initializeChatbot = showHeader => {
		if (showHeader) {
		}
	};

	signInCallback = async ({ status, errorCode, errorDescription }) => {
		const { data, directStoreSignin } = this.state;
		const { ShopifyTargetOrigin } = Session.getSettings();

		if (status) {
			this.setState({
				mask: true,
				showSignIn: false,
			});

			if (directStoreSignin) {
				const { isInWebStore } = Session.getSettings();

				if (isInWebStore) {
					const transferAuth = await Navigation.getTransferAuth();
					const shopifySuccess = {
						event: {
							type: 'SignInSuccess',
							data: {
								auth: transferAuth,
								storeContext: 'webStore',
							},
						},
					};

					Util.postMessage(shopifySuccess, ShopifyTargetOrigin);
				}
			} else {
				Session.verifyAuthAndLoad(data, this.sessionLoadCallback);
			}
		} else if (directStoreSignin) {
			const { isInWebStore } = Session.getSettings();

			if (isInWebStore) {
				const shopifyError = {
					event: {
						error: {
							message: errorDescription,
							code: errorCode,
						},
					},
				};

				Util.postMessage(shopifyError, ShopifyTargetOrigin);
			}
		}
	};

	accountTimeout = () => {
		const { optionalAuth } = this.state;

		if (!optionalAuth) {
			SierraInitializer.logout();
			ChatbotInitializer.logout();
			if (Auth0Util.isEligibleForAuth0()) {
				Session.signOut(async () => {
					Auth0Util.signOut();
				});
			} else {
				this.setState({
					showSignIn: true,
				});
			}
		}
	};

	/**
	 * Return `true` to bypass Sign-In view.
	 * @returns {boolean}
	 */
	shouldByPassSignIn = () => {
		const showActivate = Util.isInRoute('activate');
		const showTerms = Util.isInRoute('terms');
		const showTermsAccept = Util.isInRoute('showAccept');
		const showWinClientHomePage = Util.isInRoute('winclienthome');

		// bypass Sign-In view for the following cases
		return (
			showActivate === true ||
			(showTerms === true && showTermsAccept !== true) ||
			showWinClientHomePage === true
		);
	};

	render() {
		const {
			mask,
			showSignIn,
			ssoError,
			optimizelyClient,
			analyticsMetadata,
			LDProvider,
			stylesLoaded,
			auth0Error,
			auth0CustomError,
			findUsername,
			resetPassword,
			recoverpassword,
		} = this.state;
		const { isInWebStore } = Session.getSettings();
		const sessionData = Session.getData();
		const { isSignedIn } = sessionData;
		const shouldShowAuth0SignIn =
			Auth0Util.isEligibleForAuth0() &&
			!findUsername &&
			!resetPassword &&
			!recoverpassword &&
			auth0Error === false &&
			auth0CustomError === false;

		const MainViewWithLayoutAndModal = withLayoutAndModalContexts(MainView);

		if (!stylesLoaded) {
			return null;
		} else if (mask) {
			return <Spinner msg="Loading" />; // WEBAPPS-26332, shows a generic message
		}

		// Redirect to Stamps.com when on Endicia domain - render after styles are loaded
		const redirectToStamps = Util.isEndiciaDomain();
		if (redirectToStamps) {
			return <EndiciaRedirect />;
		}

		if (showSignIn) {
			if (shouldShowAuth0SignIn) {
				Auth0Util.launchSignInPage();
				return null;
			}

			return (
				<Layout.Viewport shouldScroll={showSignIn}>
					<ModalContainer>
						<ToastProvider>
							<Router basename={__BASENAME__}>
								<SignIn
									SSOError={ssoError}
									signInCallback={this.signInCallback}
									isInWebStore={isInWebStore}
									isAuth0Error={auth0Error}
									showAuth0CustomErrorPage={auth0CustomError}
								/>
							</Router>
						</ToastProvider>
					</ModalContainer>
				</Layout.Viewport>
			);
		} else if (
			isSignedIn &&
			isObject(analyticsMetadata.visitor) &&
			analyticsMetadata.visitor.id
		) {
			return (
				<ToastProvider>
					<OptimizelyProvider
						optimizely={optimizelyClient}
						user={{
							id: analyticsMetadata.visitor.id.toString(),
							attributes: analyticsMetadata.visitor,
						}}
					>
						{LDProvider ? (
							<LDProvider>
								<ModalContainer>
									<MainViewWithLayoutAndModal
										initializeChatbot={this.initializeChatbot}
									/>
									<SierraChatLauncher />
								</ModalContainer>
							</LDProvider>
						) : (
							<ModalContainer>
								<MainViewWithLayoutAndModal
									initializeChatbot={this.initializeChatbot}
								/>
								<SierraChatLauncher />
							</ModalContainer>
						)}
					</OptimizelyProvider>
				</ToastProvider>
			);
		}

		return (
			<ToastProvider>
				<MainViewWithLayoutAndModal />
			</ToastProvider>
		);
	}
}

const root = createRoot(document.getElementById('root'));
root.render(<App />);
